import { Suspense, useContext, useState } from "react";
import { useProductTranslations } from "../../hooks/TranslationHooks";
import PackageCalculator from "../PackageCalculator";
import { Col, Row } from "reactstrap";
import BjelinButton from "../BjelinButton";
import ProductStock from "../ProductStock";
import { CartContext } from "../../functional-components/CartContext";
import {
  productIsPerSquareMeter,
  productIsPurchasable,
  productPriceUnit,
  StatusCodes
} from "../../utils/ProductUtils";
import "./style.scss";
import QuantityInput from "../QuantityInput";
import { addVat } from "../../utils/VatUtils";
import { useUser } from "../../functional-components/UserContext";
import { Link } from "react-router-dom";
import Icon from "../Icon";
import CockpitEditLink from "../CockpitEditLink";
import useShowPricesWithVat from "../../hooks/useShowPricesWithVat";
import { useUserProduct } from "../../hooks/ProductHooks";
import SimpleLoadingSpinner from "../SimpleLoadingSpinner";
import { getCurrentSite } from "../../utils/SiteUtils";
import FormattedPrice from "../FormattedPrice";

const ProductPurchaseColumnInner = ({ product, children }) => {
  const { admin, b2b, isBusiness } = useUser();
  const tp = useProductTranslations();
  const { findItem, loggedAddToCart, loggedAddSampleToCart } =
    useContext(CartContext);
  const showPricesWithVat = useShowPricesWithVat();
  const [amount, setAmount] = useState(1);
  const {
    unitQuantityFactor = 0,
    sku,
    saleable,
    sampleSaleable,
    samplePrice
  } = product;
  const isPerSquareMeter = productIsPerSquareMeter(unitQuantityFactor);
  const priceUnit = productPriceUnit(unitQuantityFactor);
  const { basePrice, currency } = product.price || {};

  const addToCartHandler = e => {
    loggedAddToCart(product, amount, false, b2b);
    e.stopPropagation(true);
  };
  const addSampleToCartHandler = e => {
    loggedAddSampleToCart(product);
    e.stopPropagation(true);
  };
  const isAvailable = productIsPurchasable(product, b2b);
  const { upcoming, unavailable, discontinued, stock, hasSampleInStock } =
    product;
  const replacedBy = product?.replaced_by || [];
  const inCart = findItem(sku);

  return (
    <div className={"product-purchase-column"}>
      {children}
      {(saleable || replacedBy.length > 0 || sampleSaleable) && (
        <div className="purchase-box">
          <Row>
            {saleable && (
              <Col lg={7}>
                <div className={"amount-selector"}>
                  {isPerSquareMeter ? (
                    <>
                      <PackageCalculator
                        className={"mt-2"}
                        m2PerPackage={unitQuantityFactor}
                        pricePerPackage={
                          showPricesWithVat
                            ? addVat(parseFloat(basePrice))
                            : parseFloat(basePrice)
                        }
                        stock={stock.availableStock}
                        valuePkg={1}
                        valueCallback={setAmount}
                        currency={currency}
                        b2b={b2b}
                        isBusiness={isBusiness}
                      />
                      {/*
                  <div style={{ marginBottom: "1rem", fontSize: "0.85em" }}>
                    <Icon name="circle-info" /> Vi rekommenderar att
                    du beställer minst 5% extra i spill-marginal av detta golv.
                  </div>*/}
                    </>
                  ) : (
                    <QuantityInput
                      className={"mt-2"}
                      price={showPricesWithVat ? addVat(basePrice) : basePrice}
                      salesUnit={priceUnit}
                      product={product}
                      stock={stock.availableStock}
                      valueCallback={setAmount}
                    />
                  )}
                </div>
              </Col>
            )}
          </Row>
          <Row>
            {saleable && (
              <Col lg={7}>
                <BjelinButton
                  disabled={!isAvailable}
                  color={
                    stock.status === StatusCodes.backOrder
                      ? "bjelin-brown"
                      : "primary"
                  }
                  block={true}
                  className={"mb-2 add-to-cart-btn"}
                  icon={isAvailable ? "cart-plus" : ""}
                  onClick={addToCartHandler}
                >
                  {unavailable ||
                  (discontinued && stock.status === StatusCodes.soldOut && !b2b)
                    ? tp("shipping_status.unavailable")
                    : stock.status === StatusCodes.soldOut && !b2b
                    ? upcoming
                      ? tp("shipping_status.upcoming")
                      : tp("shipping_status.sold_out")
                    : stock.status === StatusCodes.outOfStock && !b2b
                    ? tp("shipping_status.out_of_stock")
                    : tp("add_to_cart")}
                </BjelinButton>
                {inCart && inCart.amount && (
                  <div style={{ marginBottom: "1rem", fontSize: "0.85em" }}>
                    <Icon name="circle-info" />{" "}
                    <span
                      dangerouslySetInnerHTML={{
                        __html: tp("you_have_x_of_this_product_in_your_cart", {
                          x:
                            "<b>" +
                            inCart.amount +
                            " " +
                            tp(
                              "sales_unit." +
                                (isPerSquareMeter
                                  ? "per_square_meter"
                                  : "per_package")
                            ) +
                            "</b>"
                        })
                      }}
                    />
                  </div>
                )}
              </Col>
            )}
            {sampleSaleable && (
              <Col lg={5}>
                {samplePrice && (
                  <div>
                    <h4>
                      Sample price:
                      <FormattedPrice
                        price={samplePrice.basePrice}
                        currency={samplePrice.currency}
                      />
                    </h4>
                  </div>
                )}
                <BjelinButton
                  color={"bjelin-brown"}
                  className={"mb-2 add-sample-to-cart-btn"}
                  block={true}
                  onClick={addSampleToCartHandler}
                  disabled={!hasSampleInStock}
                >
                  {hasSampleInStock
                    ? tp("order_sample")
                    : tp("sample_out_of_stock")}
                </BjelinButton>
                {!b2b && (
                  <div style={{ marginBottom: "1rem", fontSize: "0.85em" }}>
                    <Link to={tp("samples_page_path")}>
                      {tp("order_sample_text")}
                    </Link>
                  </div>
                )}
              </Col>
            )}
          </Row>
        </div>
      )}

      {product.statusText && (
        <div className={"status-text"}>
          <span>{tp("status_" + product.statusText, product.statusText)}</span>
        </div>
      )}

      <p className={"article-number"}>
        {tp("article_number")}: {sku}
        <CockpitEditLink
          collection={"products"}
          _id={product._id}
          labelKey={" "}
          wrapper={children => children}
        />
        {admin && (
          <a
            rel={"noreferrer"}
            target={"_blank"}
            href={`https://bjel.in/info/${getCurrentSite()}/art/${sku}`}
            className={"text-black ms-1"}
          >
            <Icon name={"qrcode"} />
          </a>
        )}
      </p>
      {admin && product.tags && (
        <p className={"article-tags"}>
          {product.tags.map(tag => (
            <span key={tag} style={{ marginRight: "1em" }}>
              <Icon type={"far"} name={"tag"} /> {tag}
            </span>
          ))}
        </p>
      )}

      {(saleable || !product?.replaced_by) && (
        <ProductStock
          className={"product-stock"}
          product={product}
          cartItem={inCart}
          b2b={b2b}
        />
      )}
    </div>
  );
};

const ProductPurchaseColumnWrapper = ({ sku, children }) => {
  const product = useUserProduct(sku);
  return product ? (
    <ProductPurchaseColumnInner product={product}>
      {children}
    </ProductPurchaseColumnInner>
  ) : null;
};

const ProductPurchaseColumn = ({ sku, children }) => {
  const tp = useProductTranslations();

  return sku ? (
    <Suspense
      fallback={
        <div className="center-content" aria-busy>
          <SimpleLoadingSpinner />
          <div>{tp("loading_prices")}</div>
        </div>
      }
    >
      <ProductPurchaseColumnWrapper sku={sku}>
        {children}
      </ProductPurchaseColumnWrapper>
    </Suspense>
  ) : null;
};

export default ProductPurchaseColumn;
